import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Select, Textarea, TextInput } from "../../common";
import { apiClient, apiService } from "../../../services/api";
import NodeRateCodingForm from "./NodeRateCodingForm";
import NodeSettingsForm from "./NodeSettingsForm";
import NeuralPresetSelector from "./NeuralPresetSelector";
// @ts-ignore
import NodeNeighborhoodGraph from "../NodeNeighborhoodGraph";

type RateCodingEntry = {
  id?: string | null;
  tempId: string;
  version: string;
  isActive: string;
  structuredEncodingJson: string;
  naturalLanguageEncoding: string;
  fromTemplateId: string;
  notes: string;
};

type NodeSettingEntry = {
  id?: string | null;
  tempId: string;
  neuronTemplateId: string;
  nodeSettingType: string;
  nodeSettingsName: string;
  explanation: string;
  description: string;
  boundedDataJson: string;
  nodeNeuronSettingsJson: string;
  dataClassificationId: string;
};

type NodeEffectiveSettings = {
  resolvedSettingsJson: string;
  resolvedAt: string;
  source: string;
};

type NodeKind = {
  id?: string;
  nodeKindId?: string;
  kindId?: string;
  name?: string;
  kindName?: string;
  description?: string;
  hasNeuralProfile?: boolean;
  [key: string]: any;
};

type ConnectionEntry = {
  synapseId: string;
  edgeName: string;
  weight: number;
  nodeName: string;
  nodeId: string;
  direction: "incoming" | "outgoing";
};

type TabId = "essentials" | "connections" | "advanced";

const NodeForm = ({
  kind,
  state,
  handleNodeInputChange,
  handleSaveNode,
  handleRateCodingChange,
  handleAddRateCoding,
  handleRemoveRateCoding,
  handleNodeSettingsChange,
  handleAddNodeSetting,
  handleRemoveNodeSetting,
  handleEffectiveSettingsChange,
  graphId,
  nodeId,
}: {
  kind: string;
  state: {
    form: any;
    rateCoding?: RateCodingEntry[];
    settings?: NodeSettingEntry[];
    effectiveSettings?: NodeEffectiveSettings;
    [key: string]: any;
  };
  handleNodeInputChange: (
    kind: string,
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  handleSaveNode: (kind: string) => void;
  handleRateCodingChange: (
    kind: string,
    index: number,
    field: keyof RateCodingEntry,
    value: string
  ) => void;
  handleAddRateCoding: (kind: string) => void;
  handleRemoveRateCoding: (kind: string, index: number) => void;
  handleNodeSettingsChange: (
    kind: string,
    index: number,
    field: keyof NodeSettingEntry,
    value: string
  ) => void;
  handleAddNodeSetting: (kind: string) => void;
  handleRemoveNodeSetting: (kind: string, index: number) => void;
  handleEffectiveSettingsChange?: (
    kind: string,
    field: keyof NodeEffectiveSettings,
    value: string
  ) => void;
  graphId?: string;
  nodeId?: string | null;
}) => {
  const [activeTab, setActiveTab] = useState<TabId>("essentials");
  const [nodeKinds, setNodeKinds] = useState<NodeKind[]>([]);

  // Connections tab state
  const [connections, setConnections] = useState<ConnectionEntry[]>([]);
  const [connectionsLoaded, setConnectionsLoaded] = useState(false);

  // --- Alias editor state ---
  const [aliasInput, setAliasInput] = useState("");
  const aliases: string[] = useMemo(
    () => (Array.isArray(state.form?.aliases) ? state.form.aliases : []),
    [state.form?.aliases]
  );

  const addAlias = useCallback(() => {
    const text = aliasInput.trim();
    if (!text) return;
    if (aliases.includes(text)) { setAliasInput(""); return; }
    const updated = [...aliases, text];
    handleNodeInputChange(kind, {
      target: { name: "aliases", value: updated },
    } as any);
    setAliasInput("");
  }, [aliasInput, aliases, handleNodeInputChange, kind]);

  const removeAlias = useCallback(
    (index: number) => {
      const updated = aliases.filter((_, i) => i !== index);
      handleNodeInputChange(kind, {
        target: { name: "aliases", value: updated },
      } as any);
    },
    [aliases, handleNodeInputChange, kind]
  );

  const createChangeEvent = (name: string, value: string): React.ChangeEvent<HTMLSelectElement> => {
    return {
      target: { name, value },
    } as React.ChangeEvent<HTMLSelectElement>;
  };

  const rateCodingEntries = useMemo<RateCodingEntry[]>(
    () => state.rateCoding || [],
    [state.rateCoding]
  );
  const nodeSettingsEntries = useMemo<NodeSettingEntry[]>(
    () => state.settings || [],
    [state.settings]
  );
  const [expandedEntries, setExpandedEntries] = useState<Record<string, boolean>>({});
  const [expandedSettingsEntries, setExpandedSettingsEntries] = useState<Record<string, boolean>>({});

  const getRateCodingKey = useCallback(
    (entry: RateCodingEntry, index: number) =>
      entry.id || entry.tempId || `${kind}-ratecoding-${index}`,
    [kind]
  );

  const getNodeSettingKey = useCallback(
    (entry: NodeSettingEntry, index: number) =>
      entry.id || entry.tempId || `${kind}-nodesetting-${index}`,
    [kind]
  );

  useEffect(() => {
    setExpandedEntries((prev) => {
      const next: Record<string, boolean> = {};
      rateCodingEntries.forEach((entry, index) => {
        const key = getRateCodingKey(entry, index);
        next[key] = prev[key] ?? false;
      });
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      const sameLength = prevKeys.length === nextKeys.length;
      const sameValues = sameLength && nextKeys.every((key) => prev[key] === next[key]);
      return sameValues ? prev : next;
    });
  }, [rateCodingEntries, getRateCodingKey]);

  useEffect(() => {
    setExpandedSettingsEntries((prev) => {
      const next: Record<string, boolean> = {};
      nodeSettingsEntries.forEach((entry, index) => {
        const key = getNodeSettingKey(entry, index);
        next[key] = prev[key] ?? false;
      });
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(next);
      const sameLength = prevKeys.length === nextKeys.length;
      const sameValues = sameLength && nextKeys.every((key) => prev[key] === next[key]);
      return sameValues ? prev : next;
    });
  }, [nodeSettingsEntries, getNodeSettingKey]);

  const toggleEntryExpanded = (key: string) => {
    setExpandedEntries((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const toggleSettingExpanded = (key: string) => {
    setExpandedSettingsEntries((prev) => ({ ...prev, [key]: !prev[key] }));
  };
  const formatNotesPreview = (notes?: string) => {
    if (!notes || !notes.trim()) return "—";
    const trimmed = notes.trim();
    return trimmed.length > 60 ? `${trimmed.slice(0, 57)}…` : trimmed;
  };

  const formatSettingPreview = (text?: string) => {
    if (!text || !text.trim()) return "—";
    const trimmed = text.trim();
    return trimmed.length > 60 ? `${trimmed.slice(0, 57)}…` : trimmed;
  };

  const fetchNodeKinds = async () => {
    try {
      const kindsResponse = await apiClient.get("/nodes/options/node-kinds");
      if (kindsResponse.data.success) {
        setNodeKinds(kindsResponse.data.kinds || []);
      }
    } catch (error) {
      console.error("Failed to load node kinds:", error);
    }
  };

  useEffect(() => {
    fetchNodeKinds();
  }, []);

  const nodeKindOptions = useMemo(
    () =>
      nodeKinds.map((k) => ({
        id: k.id || k.nodeKindId || k.kindId || "",
        value: k.id || k.nodeKindId || k.kindId || "",
        label: k.hasNeuralProfile === false
          ? `${k.kindName || k.name || ""} (no profile)`
          : k.kindName || k.name || "",
        kindName: k.kindName || k.name,
        hasNeuralProfile: k.hasNeuralProfile,
      })),
    [nodeKinds]
  );

  useEffect(() => {
    const kindId = state.form?.nodeKindId;
    if (!kindId) return;

    const selected = nodeKinds.find(
      (k) => (k.id || k.nodeKindId || k.kindId) === kindId
    );

    const fetchDefaults = async () => {
      try {
        if (selected?.kindName && !state.form.type) {
          handleNodeInputChange(kind, createChangeEvent("type", selected.kindName));
        }

        const resp = await apiClient.get(`/nodes/options/node-kinds/${kindId}/defaults`);
        if (resp.data.success && resp.data.defaults) {
          const d = resp.data.defaults;
          const profileJson = JSON.stringify(
            {
              excitationType: d.excitationType,
              baseThreshold: d.baseThreshold,
              baseMembraneTimeConstantMs: d.baseMembraneTimeConstantMs,
              baseRateCodingScheme: d.baseRateCodingScheme,
              baseFusionPolicy: d.baseFusionPolicy,
              basePlasticityRule: d.basePlasticityRule,
            },
            null,
            2
          );

          if (handleEffectiveSettingsChange && !state.effectiveSettings?.resolvedSettingsJson) {
            handleEffectiveSettingsChange(kind, "resolvedSettingsJson", profileJson);
            handleEffectiveSettingsChange(kind, "source", "node-kind-defaults");
          }
        }
      } catch {
        // defaults are best-effort
      }
    };

    fetchDefaults();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.form?.nodeKindId]);

  // Load connections on-demand when Connections tab is activated
  useEffect(() => {
    if (activeTab !== "connections" || !graphId || !nodeId || connectionsLoaded) return;

    const loadConnections = async () => {
      try {
        const res: any = await apiService.listSynapses({ graphId, page: 1, pageSize: 200 });
        if (res.success && res.synapses) {
          const entries: ConnectionEntry[] = [];
          for (const s of res.synapses) {
            const sid = s.synapseId || s.id;
            const edge = s.edgeName || s.relation || "—";
            const w = typeof s.weight === "number" ? s.weight : 0.5;
            if ((s.sourceNodeId || s.sourceId) === nodeId) {
              entries.push({ synapseId: sid, edgeName: edge, weight: w, nodeName: s.targetNodeName || s.targetName || "—", nodeId: s.targetNodeId || s.targetId, direction: "outgoing" });
            } else if ((s.targetNodeId || s.targetId) === nodeId) {
              entries.push({ synapseId: sid, edgeName: edge, weight: w, nodeName: s.sourceNodeName || s.sourceName || "—", nodeId: s.sourceNodeId || s.sourceId, direction: "incoming" });
            }
          }
          setConnections(entries);
        }
      } catch {
        // best-effort
      } finally {
        setConnectionsLoaded(true);
      }
    };
    loadConnections();
  }, [activeTab, graphId, nodeId, connectionsLoaded]);

  const isEditMode = !!nodeId;
  const incomingConnections = connections.filter((c) => c.direction === "incoming");
  const outgoingConnections = connections.filter((c) => c.direction === "outgoing");

  const tabs: { id: TabId; label: string; hidden?: boolean }[] = [
    { id: "essentials", label: "Essentials" },
    { id: "connections", label: `Connections${connections.length ? ` (${connections.length})` : ""}`, hidden: !isEditMode },
    { id: "advanced", label: "Advanced" },
  ];

  return (
    <>
      {/* ── Tab bar ── */}
      <div className="node-form-tabs">
        {tabs.filter((t) => !t.hidden).map((t) => (
          <button
            key={t.id}
            type="button"
            className={`node-form-tabs__tab${activeTab === t.id ? " node-form-tabs__tab--active" : ""}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════
           TAB 1 — Essentials
         ════════════════════════════════════════════════════════════════ */}
      {activeTab === "essentials" && (
        <>
          <div className="synapse-entity-modal__form-group">
            <TextInput
              id={`${kind}-name`}
              name="name"
              type="text"
              value={state.form.name}
              onChange={(event) => handleNodeInputChange(kind, event)}
              placeholder="Enter node name"
              variant="modal"
              label="Name"
            />
          </div>

          {/* Node Kind — moved up for prominence */}
          <div className="synapse-entity-modal__form-group node-kind-group">
            <Select
              id={`${kind}-nodeKindId`}
              name="nodeKindId"
              value={state.form.nodeKindId}
              onChange={(event) => handleNodeInputChange(kind, event)}
              variant="modal"
              options={nodeKindOptions}
              placeholder="None"
              label="Node Kind"
              hint="Determines default neural behavior and threshold."
            />
          </div>

          <div className="synapse-entity-modal__form-group">
            <TextInput
              id={`${kind}-type`}
              name="type"
              type="text"
              value={state.form.type}
              onChange={(event) => handleNodeInputChange(kind, event)}
              placeholder="Auto-filled from Node Kind"
              variant="modal"
              label="Type"
              hint="Auto-filled when you select a Node Kind. You can override it."
            />
          </div>

          <div className="synapse-entity-modal__form-group">
            <Textarea
              id={`${kind}-description`}
              name="description"
              rows={3}
              value={state.form.description}
              onChange={(event) => handleNodeInputChange(kind, event)}
              placeholder="Describe what this node represents"
              variant="modal"
              label="Description"
            />
          </div>

          {/* Aliases */}
          <div className="synapse-entity-modal__form-group">
            <label className="synapse-entity-modal__label" style={{ marginBottom: 4 }}>
              Aliases
              <span style={{ fontWeight: 400, fontSize: "0.85em", color: "#888", marginLeft: 6 }}>
                (alternate names for semantic search)
              </span>
            </label>
            {aliases.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                {aliases.map((alias, idx) => (
                  <span
                    key={idx}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      background: "#e8eaf6",
                      borderRadius: 16,
                      padding: "2px 10px",
                      fontSize: "0.92em",
                    }}
                  >
                    {alias}
                    <button
                      type="button"
                      onClick={() => removeAlias(idx)}
                      style={{
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        marginLeft: 4,
                        fontSize: "1em",
                        lineHeight: 1,
                        color: "#666",
                      }}
                      title="Remove alias"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="text"
                value={aliasInput}
                onChange={(e) => setAliasInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addAlias();
                  }
                }}
                placeholder="Type an alias and press Enter"
                style={{ flex: 1, padding: "6px 10px", borderRadius: 4, border: "1px solid #ccc" }}
              />
              <button
                type="button"
                onClick={addAlias}
                style={{
                  padding: "6px 14px",
                  borderRadius: 4,
                  border: "1px solid #3f51b5",
                  background: "#3f51b5",
                  color: "#fff",
                  cursor: "pointer",
                }}
              >
                Add
              </button>
            </div>
          </div>

          {/* Neural Behavior — prominent */}
          <NeuralPresetSelector
            nodeType={state.form.type || "concept"}
            currentThreshold={(() => {
              try {
                const s = JSON.parse(state.effectiveSettings?.resolvedSettingsJson || "{}");
                return s.threshold;
              } catch { return undefined; }
            })()}
            currentFusionMode={state.form.fusionMode || undefined}
            onPresetApply={(preset) => {
              handleNodeInputChange(kind, createChangeEvent("fusionMode", preset.fusionMode));
              if (handleEffectiveSettingsChange) {
                const current = (() => {
                  try { return JSON.parse(state.effectiveSettings?.resolvedSettingsJson || "{}"); } catch { return {}; }
                })();
                const updated = {
                  ...current,
                  threshold: preset.threshold,
                  rateCodingScheme: preset.rateCodingScheme,
                  fusionMode: preset.fusionMode,
                  plasticity: preset.plasticity,
                  presetId: preset.presetId,
                };
                handleEffectiveSettingsChange(kind, "resolvedSettingsJson", JSON.stringify(updated, null, 2));
                handleEffectiveSettingsChange(kind, "source", "preset");
              }
            }}
          />
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════
           TAB 2 — Connections (edit mode only)
         ════════════════════════════════════════════════════════════════ */}
      {activeTab === "connections" && isEditMode && (
        <>
          {graphId && nodeId && (
            <div style={{ marginBottom: 16 }}>
              <NodeNeighborhoodGraph graphId={graphId} nodeId={nodeId} onNodeClick={() => {}} />
            </div>
          )}

          {/* Incoming */}
          <div className="node-advanced-section" style={{ border: "none" }}>
            <p className="node-ratecoding-section__eyebrow" style={{ marginBottom: 8 }}>
              Incoming ({incomingConnections.length})
            </p>
            {incomingConnections.length === 0 && (
              <p style={{ color: "#9ca3af", fontSize: "0.9em", margin: "4px 0 12px" }}>No incoming connections.</p>
            )}
            {incomingConnections.length > 0 && (
              <div className="node-connections-list">
                {incomingConnections.map((c) => (
                  <div key={c.synapseId} className="node-connections-list__item">
                    <span className="node-connections-list__node">{c.nodeName}</span>
                    <span className="node-connections-list__edge">{c.edgeName}</span>
                    <span className="node-connections-list__weight">
                      <span className="node-connections-list__weight-bar" style={{ width: `${Math.round(c.weight * 100)}%` }} />
                      <span className="node-connections-list__weight-label">{c.weight.toFixed(2)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Outgoing */}
          <div className="node-advanced-section" style={{ border: "none" }}>
            <p className="node-ratecoding-section__eyebrow" style={{ marginBottom: 8 }}>
              Outgoing ({outgoingConnections.length})
            </p>
            {outgoingConnections.length === 0 && (
              <p style={{ color: "#9ca3af", fontSize: "0.9em", margin: "4px 0 12px" }}>No outgoing connections.</p>
            )}
            {outgoingConnections.length > 0 && (
              <div className="node-connections-list">
                {outgoingConnections.map((c) => (
                  <div key={c.synapseId} className="node-connections-list__item">
                    <span className="node-connections-list__edge">{c.edgeName}</span>
                    <span className="node-connections-list__node">{c.nodeName}</span>
                    <span className="node-connections-list__weight">
                      <span className="node-connections-list__weight-bar" style={{ width: `${Math.round(c.weight * 100)}%` }} />
                      <span className="node-connections-list__weight-label">{c.weight.toFixed(2)}</span>
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════════
           TAB 3 — Advanced
         ════════════════════════════════════════════════════════════════ */}
      {activeTab === "advanced" && (
        <>
          {/* Attributes */}
          <details className="node-advanced-section">
            <summary>Attributes</summary>
            <div className="node-advanced-section__content">
              <div className="synapse-entity-modal__form-group">
                <Textarea
                  id={`${kind}-attributes`}
                  name="attributesJson"
                  rows={6}
                  value={state.form.attributesJson}
                  onChange={(event) => handleNodeInputChange(kind, event)}
                  placeholder='{"attributes": ["tag1", "tag2"]}'
                  variant="modal"
                  label="Attributes (JSON)"
                  hint="Attributes as JSON. Can be an array or object with nested data."
                />
              </div>
            </div>
          </details>

          {/* Effective Settings */}
          <details className="node-advanced-section">
            <summary>
              Effective Settings
              {state.effectiveSettings?.source ? ` (${state.effectiveSettings.source})` : ""}
            </summary>
            <div className="node-advanced-section__content">
              <div className="synapse-entity-modal__form-group">
                <Textarea
                  id={`${kind}-resolvedSettings`}
                  name="resolvedSettingsJson"
                  rows={6}
                  value={state.effectiveSettings?.resolvedSettingsJson || ""}
                  disabled
                  placeholder="Auto-computed from Node Kind profile and Engine Preset"
                  variant="modal"
                  label="Resolved Settings (JSON)"
                  hint="Read-only. Auto-computed from your Node Kind and Engine Preset selection."
                />
              </div>
              {state.effectiveSettings?.source && (
                <div className="synapse-entity-modal__form-group">
                  <TextInput
                    id={`${kind}-source`}
                    name="source"
                    type="text"
                    value={state.effectiveSettings.source}
                    disabled
                    variant="modal"
                    label="Source"
                  />
                </div>
              )}
              {state.effectiveSettings?.resolvedAt && (
                <div className="synapse-entity-modal__form-group">
                  <TextInput
                    id={`${kind}-resolvedAt`}
                    name="resolvedAt"
                    type="text"
                    value={state.effectiveSettings.resolvedAt}
                    disabled
                    variant="modal"
                    label="Resolved At"
                  />
                </div>
              )}
            </div>
          </details>

          {/* Node Settings */}
          <details className="node-advanced-section">
            <summary>Node Settings ({nodeSettingsEntries.length})</summary>
            <div className="node-advanced-section__content">
              <div className="synapse-entity-modal__form-group node-ratecoding-section node-settings-section">
                <div className="node-ratecoding-section__header">
                  <div>
                    <p className="node-ratecoding-section__description">
                      Configure node templates, data classifications, and bounded data.
                    </p>
                  </div>
                  <Button type="button" variant="secondary" onClick={() => handleAddNodeSetting(kind)}>
                    Add setting
                  </Button>
                </div>
                {nodeSettingsEntries.length === 0 && (
                  <p className="node-ratecoding-section__empty">No node settings yet.</p>
                )}
                {nodeSettingsEntries.length > 0 && (
                  <div className="node-ratecoding-table-wrapper">
                    <table className="node-ratecoding-table">
                      <thead>
                        <tr>
                          <th aria-label="Expand" />
                          <th>Name</th>
                          <th>Type</th>
                          <th>Template ID</th>
                          <th>Data classification</th>
                          <th>Summary</th>
                          <th aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {nodeSettingsEntries.map((entry, index) => {
                          const entryKey = getNodeSettingKey(entry, index);
                          const isExpanded = Boolean(expandedSettingsEntries[entryKey]);
                          return (
                            <Fragment key={entryKey}>
                              <tr className={`node-ratecoding-table__row ${isExpanded ? "is-expanded" : ""}`}>
                                <td>
                                  <button type="button" className="node-ratecoding-table__toggle" onClick={() => toggleSettingExpanded(entryKey)}>
                                    {isExpanded ? "Hide" : "Edit"}
                                  </button>
                                </td>
                                <td>{entry.nodeSettingsName || "—"}</td>
                                <td>{entry.nodeSettingType || "—"}</td>
                                <td className="node-ratecoding-table__mono">{entry.neuronTemplateId || "—"}</td>
                                <td className="node-ratecoding-table__mono">{entry.dataClassificationId || "—"}</td>
                                <td>{formatSettingPreview(entry.description || entry.explanation)}</td>
                                <td className="node-ratecoding-table__actions">
                                  <Button type="button" variant="ghost" onClick={() => handleRemoveNodeSetting(kind, index)}>Remove</Button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <NodeSettingsForm kind={kind} index={index} entry={entry} handleNodeSettingsChange={handleNodeSettingsChange} />
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </details>

          {/* Relevance Scoring */}
          <details className="node-advanced-section">
            <summary>Relevance Scoring ({rateCodingEntries.length})</summary>
            <div className="node-advanced-section__content">
              <div className="synapse-entity-modal__form-group node-ratecoding-section">
                <div className="node-ratecoding-section__header">
                  <div>
                    <p className="node-ratecoding-section__description">
                      Define scoring schemes for this node.
                    </p>
                  </div>
                  <Button type="button" variant="secondary" onClick={() => handleAddRateCoding(kind)}>
                    Add scoring
                  </Button>
                </div>
                {rateCodingEntries.length === 0 && (
                  <p className="node-ratecoding-section__empty">No relevance scoring entries yet.</p>
                )}
                {rateCodingEntries.length > 0 && (
                  <div className="node-ratecoding-table-wrapper">
                    <table className="node-ratecoding-table">
                      <thead>
                        <tr>
                          <th aria-label="Expand" />
                          <th>Version</th>
                          <th>Status</th>
                          <th>Template ID</th>
                          <th>Notes</th>
                          <th aria-label="Actions" />
                        </tr>
                      </thead>
                      <tbody>
                        {rateCodingEntries.map((entry, index) => {
                          const entryKey = getRateCodingKey(entry, index);
                          const isExpanded = Boolean(expandedEntries[entryKey]);
                          return (
                            <Fragment key={entryKey}>
                              <tr className={`node-ratecoding-table__row ${isExpanded ? "is-expanded" : ""}`}>
                                <td>
                                  <button type="button" className="node-ratecoding-table__toggle" onClick={() => toggleEntryExpanded(entryKey)}>
                                    {isExpanded ? "Hide" : "Edit"}
                                  </button>
                                </td>
                                <td>{entry.version || "—"}</td>
                                <td>{entry.isActive === "false" ? "Inactive" : "Active"}</td>
                                <td className="node-ratecoding-table__mono">{entry.fromTemplateId || "—"}</td>
                                <td>{formatNotesPreview(entry.notes)}</td>
                                <td className="node-ratecoding-table__actions">
                                  <Button type="button" variant="ghost" onClick={() => handleRemoveRateCoding(kind, index)}>Remove</Button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <NodeRateCodingForm kind={kind} index={index} entry={entry} handleRateCodingChange={handleRateCodingChange} />
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </details>

        </>
      )}

      {/* ── Always visible: error/success + save ── */}
      {state.error && (
        <p className="synapse-entity-modal__error">{state.error}</p>
      )}
      {state.success && (
        <p className="synapse-entity-modal__success">{state.success}</p>
      )}
      <div className="synapse-entity-modal__footer">
        <Button
          type="button"
          variant="primary"
          onClick={() => handleSaveNode(kind)}
          disabled={state.saving}
        >
          {state.saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </>
  );
};

export default NodeForm;
